import { useEffect, useState } from 'react';
import { Box, ListItemIcon, ListItemText, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Layout } from '@roc/feature-app-core';
import { Tabs, Paper, GridColumnProps } from '@roc/ui';
import { observer } from 'mobx-react';
import { ProtectiveAdvancesGrid } from '../protectiveAdvancesGrid';
import { filterProps, noOpComparator } from '@roc/ui/utils';
import { CounterPartyExpensesTab, REFUNDS_TABS } from '../../types/types';
import { useDocumentStore } from '@roc/feature-documents';
import { CloudDownload, Check } from '@material-ui/icons';
import { insertIf } from '@roc/feature-utils';
import { MarkAsPaidModal } from '../payables/markAsPaidModal';
import { useRefundsDashboardStore } from '../../hooks/useRefundsDashboardStore';
import { RefundsToolbar } from './refundsToolbar';

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '1400px',
  },
  paper: {
    boxShadow: 'none !important',
    border: '1px solid #cdcdcd',
    padding: '0px',
    borderRadius: '0 !important',
  },
}));

export const RefundsDashboard = observer(() => {
  const classes = useStyles();
  const { refundsDashboardStore } = useRefundsDashboardStore();
  const { tab, payablesApprovedStore } = refundsDashboardStore;
  const { documentStore } = useDocumentStore();
  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = useState(false);
  const [payableRefNumber, setPayableRefNumber] = useState<string>('');
  const [actionSelectedRecord, setActionSelectedRecord] = useState(null);

  useEffect(() => {
    refundsDashboardStore.fetchEntityNames();
    return () => {
      refundsDashboardStore.reset();
    };
  }, []);

  const handleTabChange = (_, i) => {
    refundsDashboardStore.setTab(REFUNDS_TABS[i].value)
  }

  const openMarkAsPaidModal = () => {
    setShowMarkAsPaidModal(true);
  }

  const closeMarkAsPaidModal = () => {
    setPayableRefNumber('');
    setShowMarkAsPaidModal(false);
  };

  const handleMarkAsPaidConfirm = async () => {
    await payablesApprovedStore.markAsPaid(actionSelectedRecord?.recordId, payableRefNumber);
    refundsDashboardStore.counterPartyExpenseGridStore.resetAndFetchGridData();
    closeMarkAsPaidModal();
  }

  const actions = (record, handleMenuClose) => {
    return (
      [
        <MenuItem
          disabled={!record.documentId}
          onClick={() => {
            const document = {
              taskId: record.documentId,
              originalFileName: record.originalFileName,
            };
            documentStore.downloadDocumentOriginalNameById(document);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CloudDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download Invoice</ListItemText>
        </MenuItem>,
        ...insertIf(tab === CounterPartyExpensesTab.PENDING_REFUNDS, [
          <MenuItem
            onClick={() => {
              setActionSelectedRecord(record);
              openMarkAsPaidModal()
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <Check fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mark as Paid</ListItemText>
          </MenuItem>
        ])
      ]
    );
  }

  const COLUMNS: GridColumnProps[] = [
    {
      field: 'loanId',
      headerName: 'Loan Id',
      width: 90,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'entity',
      headerName: 'Payee',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'invoice',
      headerName: 'Invoice #',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'feeType',
      headerName: 'Type',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'receivableStatus',
      headerName: 'Receivable Status',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      minWidth: 125,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'createdDate',
      headerName: 'Creation Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'leadId',
      headerName: 'Action',
      minWidth: 80,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      width: 80,
      pinned: 'right',
      lockPinned: true,
    },
  ];
  return (
    <Layout title="Refunds" containerClass={classes.container}>
      <Box pb={2}>
        <Tabs
          selectedTab={REFUNDS_TABS.findIndex(t => t.value == tab)}
          tabs={Object.values(REFUNDS_TABS)}
          onTabChange={handleTabChange}
        />
      </Box>
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex={1}>
          <Paper className={classes.paper}>
            <ProtectiveAdvancesGrid
              counterPartyExpenseStore={refundsDashboardStore}
              columns={COLUMNS}
              toolbar={<RefundsToolbar />}
              selectModeEnabled={tab === CounterPartyExpensesTab.PENDING_REFUNDS}
              actions={actions}
            />
          </Paper>
        </Box>
      </Box>
      <MarkAsPaidModal
        payableRefNumber={payableRefNumber}
        setPayableRefNumber={setPayableRefNumber}
        showMarkAsPaidModal={showMarkAsPaidModal}
        closeMarkAsPaidModal={closeMarkAsPaidModal}
        handleMarkAsPaidConfirm={handleMarkAsPaidConfirm}
      />
    </Layout>
  );
});
